import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AppService } from 'src/app/core/services/app.service';
import { FocuslinkService } from 'src/app/core/services/focuslink.service';
import { OOVenueService } from 'src/app/core/services/venue-service';
import { ChartLegendPosition } from 'src/app/shared/models/enums/ChartLegendPosition.enum';
import { SalesSummary } from 'src/app/shared/models/SalesSummary.model';

@Component({
	selector: 'app-labor-dash',
	templateUrl: './labor-dash.component.html',
	styleUrls: ['../../commandcenter.scss'],
})
export class LaborDashComponent implements OnInit, OnChanges {
	@Input() checkDate: string;
	@Output() checkDateChange = new EventEmitter<string>();

	@Input() currentDaySalesSummary: SalesSummary;
	@Output() currentDaySalesSummaryChange = new EventEmitter<SalesSummary>();

	laborDoughnut = {
		options: undefined,
		data: undefined,
	};

	laborByHour = {
		options: undefined,
		data: undefined,
	};

	metrics = {
		laborCost: 0,
		laborCostPercent: 0,
		laborPercent: 0,
		laborPercentPercent: 0,
		currentHour: 0,
		currentHourPercent: 0,
		currentDayHourCost: 0,
		lastCheckDate: undefined,
		recordsRemaining: 0,
		loadComplete: false,
		onClockEmployees: 0,
		noBreakEmployees: 0,
		onClockEmployeeList: [],
		overtime: 0,
		overtimePercent: 0,
		historicalOvertime: 0,
	};

	constructor(public app: AppService, public venueService: OOVenueService, private focuslink: FocuslinkService) {}

	ngOnInit(): void {
		console.log('init');
		this.buildLaborDoughnut();
		this.buildMetrics();
		this.buildLaborByHour();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.currentDaySalesSummary && this.currentDaySalesSummary?.checkDate) {
			this.buildLaborDoughnut();
			this.buildMetrics();
			this.buildLaborByHour();
		}
		if (changes.checkDate) {
			this.metrics.loadComplete = false;
		}
	}

	async buildLaborDoughnut() {
		this.currentDaySalesSummary.laborJobSummary?.sort((a, b) => (a.jobName > b.jobName ? 1 : -1));
		this.currentDaySalesSummary.laborJobSummary?.forEach((job, idx) => {
			job.chartColor = this.app.references.chartColorsGoogle[idx];
		});
		const data = {
			labels: this.currentDaySalesSummary.laborJobSummary?.map((i) => i.jobName),
			datasets: [
				{
					label: 'Jobs',
					data: this.currentDaySalesSummary.laborJobSummary?.map((i) => i.laborPercentage),
					fill: true,
					backgroundColor: this.currentDaySalesSummary.laborJobSummary?.map((i) => i.chartColor),
				},
			],
		};

		this.laborDoughnut.data = data;
		this.laborDoughnut.options = this.app.references.chartConfig(ChartLegendPosition.NONE, false, false);
	}

	buildLaborByHour() {
		const data = {
			labels: this.currentDaySalesSummary.laborHourSummary?.map((i) => this.app.moment(i.startTime).format('hh A')),
			datasets: [
				{
					label: 'Hours',
					data: this.currentDaySalesSummary.laborHourSummary?.map((i) => i.cost),
					fill: true,
					borderColor: this.app.references.colorPallete.primary,
					backgroundColor: this.app.references.transparentize(this.app.references.colorPallete.primary, 0.75),
					yAxisID: 'y',
					tension: 0.4,
				},
			],
		};

		this.laborByHour.data = data;
		this.laborByHour.options = this.app.references.chartConfig(ChartLegendPosition.NONE, false, false);
	}

	async buildMetrics() {
		this.metrics.loadComplete = false;
		const currentDaySummary = this.venueService.salesSummaryList.value.find((i) => i.checkDate === this.checkDate);
		const currentDayTimecards = this.venueService.timecardRecordList.value.filter((i) => i.checkDate === this.checkDate);
		const nonCurrentTimecards = this.venueService.timecardRecordList.value.filter((i) => i.checkDate !== this.checkDate);

		const currentDayMetrics = {
			checkCount: currentDaySummary?.checkCount,
			averageCheck: currentDaySummary?.averageCheck,
			netSales: currentDaySummary?.netSales,
		};

		const metricVals = {
			laborCost: 0,
			laborPercent: 0,
			currentHour: 0,
			currentHourRecordsParsed: 0,
			overtime: 0,
		};

		this.venueService.salesSummaryList.value.forEach((day) => {
			if (day.checkDate !== currentDaySummary.checkDate) {
				metricVals.laborCost += day.laborCost;
				metricVals.laborPercent += day.laborPercentage;
				console.log(day);
				const laborHour = day.laborHourSummary?.find((i) => i.hour === +this.app.moment().format('H'));
				if (laborHour) {
					metricVals.currentHour += laborHour.cost;
					metricVals.currentHourRecordsParsed += 1;
				}
			}
		});

		this.metrics.onClockEmployeeList = [];
		this.metrics.onClockEmployees = 0;
		currentDayTimecards.forEach((tcr) => {
			if (tcr.timeIn && !tcr.timeOut) {
				this.metrics.onClockEmployees += 1;
				if (!tcr.breaks || tcr.breaks.length === 0) {
					this.metrics.noBreakEmployees += 1;
				}
				const onClockRec = this.metrics.onClockEmployeeList.find((i) => i.name === tcr.job.name);
				if (!onClockRec) {
					this.metrics.onClockEmployeeList.push({
						name: tcr.job.name,
						count: 1,
					});
				} else {
					onClockRec.count += 1;
				}
			}

			if (tcr.dailyOvertime) {
				this.metrics.overtime += tcr.dailyOvertime;
			}
			if (tcr.weeklyOvertime) {
				this.metrics.overtime += tcr.weeklyOvertime;
			}
		});

		nonCurrentTimecards.forEach((tcr) => {
			if (tcr.dailyOvertime) {
				this.metrics.historicalOvertime += tcr.dailyOvertime;
			}
			if (tcr.weeklyOvertime) {
				this.metrics.historicalOvertime += +tcr.weeklyOvertime;
			}
		});

		this.metrics.historicalOvertime = this.metrics.historicalOvertime / (this.venueService.salesSummaryList.value.length - 1);
		this.metrics.overtimePercent =
			metricVals.overtime === 0 ? this.metrics.overtime / this.metrics.historicalOvertime : this.metrics.overtime / this.metrics.historicalOvertime - 1;

		this.metrics.laborCost = metricVals.laborCost / (this.venueService.salesSummaryList.value.length - 1);
		this.metrics.laborCostPercent =
			this.currentDaySalesSummary?.laborCost === 0
				? this.currentDaySalesSummary?.laborCost / this.metrics?.laborCost
				: this.currentDaySalesSummary?.laborCost / this.metrics?.laborCost - 1;

		this.metrics.laborPercent = metricVals.laborPercent / (this.venueService.salesSummaryList.value.length - 1);
		this.metrics.laborPercentPercent =
			this.currentDaySalesSummary.checkCount === 0
				? this.currentDaySalesSummary?.laborPercentage / this.metrics?.laborPercent
				: this.currentDaySalesSummary?.laborPercentage / this.metrics?.laborPercent - 1;

		this.metrics.currentDayHourCost = this.currentDaySalesSummary.laborHourSummary?.find((i) => i.hour == +this.app.moment().format('H'))?.cost;
		this.metrics.currentHour = metricVals.currentHour / metricVals.currentHourRecordsParsed;
		this.metrics.currentHourPercent =
			this.metrics.currentDayHourCost === 0
				? this.metrics.currentDayHourCost / this.metrics.currentHour
				: this.metrics.currentDayHourCost / this.metrics.currentHour - 1;

		this.metrics.lastCheckDate = this.venueService.salesSummaryList.value[this.venueService.salesSummaryList.value.length - 1].checkDate;
		this.metrics.recordsRemaining = this.app.moment(this.checkDate, 'MMDDYYYY').diff(this.app.moment(this.metrics.lastCheckDate, 'MMDDYYYY'), 'days');
		this.metrics.loadComplete = true;
	}
}
